<template>
  <div
    v-if="pager"
    class="d-flex align-items-center justify-content-between flex-wrap flex-col md:flex-row"
  >
    <div class="flex items-end gap-3 md:gap-1">
      <div v-if="pager.meta.pagination" class="md:mb-xs-2">
        Showing
        <b>{{ pager.meta.pagination.from }}-{{ pager.meta.pagination.to }}</b>
        of
        <b>{{ pager.meta.pagination.total_objects }}</b>
      </div>
      <PaginationLimit
        v-if="!noPageLength"
        :pager="pager"
        :limit-options="limitOptions"
        :default-limit="defaultLimit"
        @update:data="updateData"
      />
    </div>
    <div
      class="pagination-buttons-cont text-xs max-w-full flex -space-x-px h-10"
    >
      <div
        v-for="(option, i) in paginationOptions"
        :key="option.label + i"
        class="pagination-button !cursor-pointer flex items-center justify-center !px-4 h-[46px] min-w-[46px] leading-tight border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
        :class="{
          'text-gray-900 bg-white': option.label != currentPage,
          'md:!bg-gray-50 sm:md:!bg-gray-50 !text-gray-300': option.disabled,
          '!text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white':
            option.label == currentPage,
          single: paginationOptions.length === 1
        }"
        :data-cy="`paginationButton:${option.label}`"
        @click="handlePage(option.value)"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Pagination from "@/lib/pagination";

import PaginationLimit from "./PaginationLimit.vue";
import { useBreakpoints } from "./useBreakpoints";

const props = defineProps<{
  pager?: Pagination;
  scrollToSelector?: string;
  limitOptions?: number[];
  defaultLimit?: number;
  noPageLength?: boolean;
}>();
const emit = defineEmits(["update:data"]);
const DOTS = "...";

const breakpoint = useBreakpoints();

const currentPage = computed(() => {
  return props.pager?.meta?.pagination?.page || 1;
});

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const getPaginationRange = (totalPages) => {
  const siblingCount = 1;

  const totalPageCount = totalPages;

  // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
  const totalPageNumbers =
    siblingCount + breakpoint.type.value === "mobile" ? 2 : 5;

  /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
  if (totalPageNumbers >= totalPageCount) {
    return range(1, totalPageCount);
  }

  /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
  const leftSiblingIndex = Math.max(currentPage.value - siblingCount, 1);
  const rightSiblingIndex = Math.min(
    currentPage.value + siblingCount,
    totalPageCount
  );

  /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

  const firstPageIndex = 1;
  const lastPageIndex = totalPageCount;

  /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount =
      (breakpoint.type.value === "mobile" ? 1 : 3) + 2 * siblingCount;
    const leftRange = range(1, leftItemCount);

    return [...leftRange, DOTS, totalPageCount];
  }

  /*
    	Case 3: No right dots to show, but left dots to be shown
    */
  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount =
      (breakpoint.type.value === "mobile" ? 1 : 3) + 2 * siblingCount;
    const rightRange = range(
      totalPageCount - rightItemCount + 1,
      totalPageCount
    );
    return [firstPageIndex, DOTS, ...rightRange];
  }

  /*
    	Case 4: Both left and right dots to be shown
    */
  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = range(leftSiblingIndex, rightSiblingIndex);
    return breakpoint.type.value === "mobile"
      ? [firstPageIndex, DOTS, middleRange[1], DOTS, lastPageIndex]
      : [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
  }
};

const paginationOptions = computed(() => {
  if (!props.pager?.meta?.pagination) return [];
  const { meta, goToPage } = props.pager;
  const { pagination } = meta;

  // const labels = {
  //   first: breakpoint.type.value === "mobile" ? "<<" : "<< First",
  //   last: breakpoint.type.value === "mobile" ? ">>" : "Last >>",
  //   next: breakpoint.type.value === "mobile" ? ">" : "Next >",
  //   prev: breakpoint.type.value === "mobile" ? "<" : "< Previous"
  // };

  const options: any[] = [];
  // const options: any[] = [{ label: labels.first, value: first }];

  // options.push({
  //   label: labels.prev,
  //   value: prev,
  //   disabled: !pagination.links.prev
  // });

  const range = getPaginationRange(pagination.total_pages) || [];

  range.forEach((page) => {
    options.push({
      label: page,
      value: page !== DOTS ? () => goToPage(page) : null,
      disabled: page === DOTS
    });
  });

  // options.push({
  //   label: labels.next,
  //   value: next,
  //   disabled: !pagination.links.next
  // });
  // options.push({ label: labels.last, value: last });
  return options;
});

const handlePage = async (pageAction) => {
  if (!pageAction) return;
  if (props.scrollToSelector) {
    const el: any = document.querySelector(props.scrollToSelector);
    const box = el.getBoundingClientRect();
    const isInView = box.top < window.innerHeight && box.bottom >= 0;
    if (!isInView) {
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }
  const response = await pageAction();
  if (response) {
    emit("update:data", response.data.data);
  }
};

const updateData = (res) => emit("update:data", res);
</script>

<style scoped>
.per-page-select {
  width: 70px;
}
.pagination-button {
  &:first-of-type {
    border-radius: 0.5rem 0 0 0.5rem;
  }
  &:last-of-type {
    border-radius: 0 0.5rem 0.5rem 0;
  }
  &.single {
    border-radius: 0.5rem !important;
  }
}
</style>
